import { declareParams, JsonApiRequestError } from '@farfetched/core';
import { zodContract } from '@farfetched/zod';
import { applyTimeZone, RegistryParams, RegistryQuery } from 'shared/lib';
import { createAuthQuery } from '../auth-http-client';
import {
	createPaginatedSchema,
	BetReport,
	BetReportAdditionalData,
	betReportSchema,
	betReportAdditionalDataSchema,
} from '../types';

export type OriginalBetReportRegistryParams = {
	page?: number;
	limit?: number;
	currencies?: string[];
	accountIds?: string[];
	cashdeskIds?: string[];
	gameIds?: string[];
	dateFrom?: string;
	dateTo?: string;
	timeZone?: number;
};

export type BetReportRegistryFilters = {
	currencies: string[];
	accountIds: number[];
	cashdeskIds: number[];
	gameIds: number[];
	dateFrom: string;
	dateTo: string;
	timeZone: number;
};

export type BetReportRegistryParams = RegistryParams<BetReportRegistryFilters>;

export type BetReportRegistryQuery = RegistryQuery<
	BetReport,
	BetReportRegistryFilters,
	JsonApiRequestError,
	BetReportAdditionalData
>;

export const createBetReportRegistryQuery = (): BetReportRegistryQuery => {
	const query = createAuthQuery({
		params: declareParams<BetReportRegistryParams>(),
		request: {
			method: 'GET',
			url: '/report/bet',
			query: (params) => ({
				page: params.page,
				limit: params.limit,
				sortField: params.sortField ?? undefined,
				sortDirection: params.sortDirection,
				currencies: params.filters.currencies?.length === 0 ? undefined : params.filters.currencies,
				accountIds: [...params.filters.accountIds.map(String)],
				cashdeskIds: [...params.filters.cashdeskIds.map(String)],
				gameIds: [...params.filters.gameIds.map(String)],
				dateFrom: params.filters.dateFrom,
				dateTo: params.filters.dateTo,
				timeZone: params.filters.timeZone,
			}),
		},
		response: {
			contract: zodContract(createPaginatedSchema(betReportSchema, betReportAdditionalDataSchema)),
			mapData: ({ result, params }) => ({
				...result,
				rows: result.rows.map<BetReport>((row) => ({
					...row,
					date: applyTimeZone(row.date, params.filters.timeZone),
				})),
			}),
		},
	});

	return query;
};
